import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TelegramLoginPanel from '../../components/TelegramLoginPanel';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import type { WebSessionUser } from '../../types';

export default function AuthScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    submitAuthForm,
    loginEmail,
    setLoginEmail,
    loginPassword,
    setLoginPassword,
    authPending,
    authMode,
    authNotice,
    toggleAuthMode,
    clearError,
    setSessionUser,
  } = useWebsiteAppContext();
  const [telegramAvailable, setTelegramAvailable] = useState(true);

  function handleTelegramAuthenticated(user: WebSessionUser): void {
    clearError();
    setSessionUser(user);
    navigate('/');
  }

  return (
    <div className="flex min-h-[calc(100vh-var(--web-header-height)-var(--web-footer-height))] justify-center rounded-xl bg-primary px-5 py-10 shadow-ambient sm:px-8">
      <div className="flex w-full max-w-[520px] flex-col justify-center">
        <div className="mb-8">
          <h2 className="editorial-title mb-2 text-3xl font-bold text-text">
            {authMode === 'login' ? t('website.auth.welcomeBack') : t('website.auth.createAccount')}
          </h2>
          <p className="text-text-secondary">
            {authMode === 'login'
              ? t('website.auth.signInSubtitle')
              : t('website.auth.registerSubtitle')}
          </p>
        </div>

        {authNotice ? (
          <p className="mb-5 rounded-lg bg-secondary px-4 py-3 text-sm text-text" role="status">
            {authNotice}
          </p>
        ) : null}

        {/* Telegram sign-in (primary) */}
        <TelegramLoginPanel
          mode={authMode}
          onAuthenticated={handleTelegramAuthenticated}
          onUnavailable={() => setTelegramAvailable(false)}
        />

        {telegramAvailable ? (
          <div className="my-8 flex items-center gap-4">
            <div className="h-px flex-1 bg-border-solid opacity-20" />
            <span className="text-xs font-bold uppercase tracking-widest text-text-secondary opacity-60">
              {t('website.auth.orContinueWithEmail')}
            </span>
            <div className="h-px flex-1 bg-border-solid opacity-20" />
          </div>
        ) : null}

        {/* Email/Password Form */}
        <form className="grid gap-5" onSubmit={(event) => void submitAuthForm(event)}>
          <div className="grid gap-2">
            <label className="text-xs font-bold uppercase tracking-wider text-text-secondary">
              {t('website.auth.emailLabel')}
            </label>
            <input
              type="email"
              className="w-full rounded-lg border-0 bg-secondary px-4 py-3 text-text transition-all focus:bg-primary focus:ring-2 focus:ring-accent/20"
              placeholder={t('website.auth.emailPlaceholder')}
              value={loginEmail}
              onChange={(event) => setLoginEmail(event.target.value)}
              required
            />
          </div>
          <div className="grid gap-2">
            <label className="text-xs font-bold uppercase tracking-wider text-text-secondary">
              {t('website.auth.passwordLabel')}
            </label>
            <input
              type="password"
              className="w-full rounded-lg border-0 bg-secondary px-4 py-3 text-text transition-all focus:bg-primary focus:ring-2 focus:ring-accent/20"
              placeholder={t('website.auth.passwordPlaceholder')}
              value={loginPassword}
              onChange={(event) => setLoginPassword(event.target.value)}
              minLength={8}
              required
            />
          </div>

          <button
            className="mt-2 flex w-full items-center justify-center rounded-lg bg-accent px-6 py-4 font-semibold text-on-accent transition-all hover:opacity-90 disabled:opacity-50"
            type="submit"
            disabled={authPending}
            style={{
              background: 'linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-container) 100%)',
            }}
          >
              {authPending
                ? authMode === 'login'
                ? t('website.auth.loggingIn')
                : t('website.auth.registering')
              : authMode === 'login'
                ? t('website.auth.login')
                : t('website.auth.register')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            className="text-sm font-medium text-accent hover:underline"
            onClick={toggleAuthMode}
          >
            {authMode === 'login'
              ? t('website.auth.switchToRegister')
              : t('website.auth.switchToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
}
